/**
 * Product Stock Status Widget
 * Updates stock status display when variant changes
 */

class ProductStockStatus {
    constructor() {
        this.stockStatusElement = document.querySelector('[data-stock-status]');

        if (!this.stockStatusElement) return;

        this.highThreshold = parseInt(this.stockStatusElement.dataset.highThreshold) || 50;
        this.lowThreshold = parseInt(this.stockStatusElement.dataset.lowThreshold) || 20;
        this.textElement = this.stockStatusElement.querySelector('[data-stock-status-text]');

        // Store original text templates from the rendered HTML
        this.storeOriginalTexts();

        this.init();
    }

    storeOriginalTexts() {
        // Get text templates and colors from data attributes
        this.highStockText = this.stockStatusElement.dataset.highText || 'In Stock - [quantity] available';
        this.mediumStockText = this.stockStatusElement.dataset.mediumText || 'Limited Stock - Only [quantity] left';
        this.lowStockText = this.stockStatusElement.dataset.lowText || 'Low Stock - Hurry, only [quantity] remaining!';

        this.highStockColor = this.stockStatusElement.dataset.highColor || '#2e7d32';
        this.mediumStockColor = this.stockStatusElement.dataset.mediumColor || '#f57c00';
        this.lowStockColor = this.stockStatusElement.dataset.lowColor || '#c62828';

        this.highStockBg = this.stockStatusElement.dataset.highBg || '#e8f5e9';
        this.mediumStockBg = this.stockStatusElement.dataset.mediumBg || '#fff3e0';
        this.lowStockBg = this.stockStatusElement.dataset.lowBg || '#ffebee';
    }

    init() {
        // Get product ID from the widget
        this.productId = this.stockStatusElement.dataset.productId;

        // Fetch product data to get accurate inventory across all locations
        this.fetchProductData();

        // Monitor inventory data attribute changes
        this.observeInventoryChanges();

        // Listen for variant change through hidden input
        this.observeVariantInput();

        // Listen for variant change events (if custom events are dispatched)
        document.addEventListener('variant:change', (event) => {
            if (event.detail && event.detail.variant) {
                this.updateStockStatus(event.detail.variant);
            }
        });

        // Also listen for custom inventory update events
        document.addEventListener('inventory:update', (event) => {
            if (event.detail && typeof event.detail.quantity !== 'undefined') {
                this.updateStockStatusByQuantity(event.detail.quantity);
            }
        });
    }

    async fetchProductData() {
        // First, try to use the existing window.product_inven_array if available
        const productId = this.productId;
        const inventoryArray = window[`product_inven_array_${productId}`];

        if (inventoryArray) {
            // Use the existing inventory array from the product page
            this.inventoryArray = inventoryArray;

            // Get current variant ID and update stock status
            const currentVariantId = this.stockStatusElement.dataset.variantId;
            const quantity = parseInt(inventoryArray[currentVariantId]);

            if (!isNaN(quantity)) {
                this.updateStockStatusByQuantity(quantity);
            }
            return;
        }

        // Fallback: fetch product data via API
        try {
            const productHandle = window.location.pathname.split('/products/')[1]?.split('?')[0];
            if (!productHandle) return;

            const response = await fetch(`/products/${productHandle}.js`);
            const productData = await response.json();

            // Store product data for later use
            this.productData = productData;

            // Create inventory array from product data
            this.inventoryArray = {};
            productData.variants.forEach(variant => {
                this.inventoryArray[variant.id] = variant.inventory_quantity || 0;
            });

            // Get current variant ID and update stock status
            const currentVariantId = parseInt(this.stockStatusElement.dataset.variantId);
            const quantity = this.inventoryArray[currentVariantId];

            if (quantity !== undefined) {
                this.updateStockStatusByQuantity(quantity);
            }
        } catch (error) {
            console.error('Error fetching product data for stock status:', error);
            // Fallback to initial value from Liquid if fetch fails
            const initialQuantity = parseInt(this.stockStatusElement.dataset.initialQuantity);
            if (!isNaN(initialQuantity)) {
                this.updateStockStatusByQuantity(initialQuantity);
            }
        }
    }

    observeVariantInput() {
        // Watch for changes in the variant input field
        const variantInput = document.querySelector('input[name="id"]');

        if (!variantInput) return;

        variantInput.addEventListener('change', () => {
            const variantId = variantInput.value;
            this.updateStockStatusByVariantId(variantId);
        });

        // Also trigger initial update
        if (variantInput.value) {
            this.updateStockStatusByVariantId(variantInput.value);
        }
    }

    updateStockStatusByVariantId(variantId) {
        if (!variantId) return;

        // Try to use stored inventoryArray first
        if (this.inventoryArray && this.inventoryArray[variantId] !== undefined) {
            const quantity = parseInt(this.inventoryArray[variantId]);
            if (!isNaN(quantity)) {
                this.updateStockStatusByQuantity(quantity);
                return;
            }
        }

        // Fallback: Get inventory from window.product_inven_array
        if (this.productId) {
            const inventoryArrayName = `product_inven_array_${this.productId}`;
            const inventoryArray = window[inventoryArrayName];

            if (inventoryArray && inventoryArray[variantId] !== undefined) {
                const quantity = parseInt(inventoryArray[variantId]);
                if (!isNaN(quantity)) {
                    this.updateStockStatusByQuantity(quantity);
                }
            }
        }
    }

    observeInventoryChanges() {
        // Watch for changes in the inventory display element
        const inventoryElement = document.querySelector('[data-inventory] .productView-info-value');

        if (!inventoryElement) return;

        // Use MutationObserver to detect text changes
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList' || mutation.type === 'characterData') {
                    this.extractAndUpdateQuantity();
                }
            });
        });

        observer.observe(inventoryElement, {
            childList: true,
            characterData: true,
            subtree: true
        });

        // Also watch for data-inventory-quantity attribute changes on quantity input
        const quantityInput = document.querySelector('input[name="quantity"][data-inventory-quantity]');

        if (quantityInput) {
            const inputObserver = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'data-inventory-quantity') {
                        const quantity = parseInt(quantityInput.dataset.inventoryQuantity);
                        if (!isNaN(quantity)) {
                            this.updateStockStatusByQuantity(quantity);
                        }
                    }
                });
            });

            inputObserver.observe(quantityInput, {
                attributes: true,
                attributeFilter: ['data-inventory-quantity']
            });
        }
    }

    extractAndUpdateQuantity() {
        // Try to extract quantity from the inventory display
        const inventoryElement = document.querySelector('[data-inventory] .productView-info-value');

        if (!inventoryElement) return;

        const text = inventoryElement.textContent.trim();
        const match = text.match(/(\d+)/);

        if (match) {
            const quantity = parseInt(match[1]);
            this.updateStockStatusByQuantity(quantity);
        } else if (text.toLowerCase().includes('out of stock')) {
            this.updateStockStatusByQuantity(0);
        }
    }
    
    getOriginalText(status) {
        const statusElement = this.stockStatusElement;
        const dataAttr = `data-${status}-stock-text`;
        
        // Try to get from data attribute, fallback to current text
        if (statusElement.hasAttribute(dataAttr)) {
            return statusElement.getAttribute(dataAttr);
        }
        
        // Fallback to default texts
        const defaults = {
            high: 'In Stock - [quantity] available',
            medium: 'Limited Stock - Only [quantity] left',
            low: 'Low Stock - Hurry, only [quantity] remaining!'
        };
        
        return defaults[status] || '';
    }
    
    updateStockStatus(variant) {
        if (!variant.inventory_management) {
            // If inventory is not tracked, hide the widget or show default message
            this.stockStatusElement.style.display = 'none';
            return;
        }

        // Try to get quantity from inventoryArray first
        if (this.inventoryArray && variant.id) {
            const quantity = parseInt(this.inventoryArray[variant.id]);
            if (!isNaN(quantity)) {
                this.updateStockStatusByQuantity(quantity);
                return;
            }
        }

        // Fallback: use variant.inventory_quantity
        const quantity = variant.inventory_quantity || 0;
        this.updateStockStatusByQuantity(quantity);
    }
    
    updateStockStatusByQuantity(quantity) {
        if (quantity === null || quantity === undefined) return;

        this.stockStatusElement.style.display = 'block';

        let status, text, icon, color, bg;

        if (quantity > this.highThreshold) {
            status = 'high';
            text = this.highStockText;
            color = this.highStockColor;
            bg = this.highStockBg;
            icon = '✓';
        } else if (quantity >= this.lowThreshold) {
            status = 'medium';
            text = this.mediumStockText;
            color = this.mediumStockColor;
            bg = this.mediumStockBg;
            icon = '!';
        } else {
            status = 'low';
            text = this.lowStockText;
            color = this.lowStockColor;
            bg = this.lowStockBg;
            icon = '⚠';
        }

        // Update classes
        this.stockStatusElement.className = `productView-stockStatus productView-stockStatus--${status}`;
        this.stockStatusElement.setAttribute('data-stock-status', status);

        // Update CSS variables for colors
        this.stockStatusElement.style.setProperty('--stock-color', color);
        this.stockStatusElement.style.setProperty('--stock-bg', bg);

        // Update text with quantity
        if (this.textElement) {
            this.textElement.textContent = text.replace('[quantity]', quantity);
        }

        // Update icon if exists
        const iconElement = this.stockStatusElement.querySelector('.productView-stockStatus__icon');
        if (iconElement) {
            iconElement.textContent = icon;
        }
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new ProductStockStatus();
    });
} else {
    new ProductStockStatus();
}

